import React, { useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { FaStar, FaUser, FaCalendar, FaThumbsUp } from 'react-icons/fa';
import ThemeContext from '../contexts/ThemeContext';
import Navbar from '../components/Navbar';

const API_URL = 'https://ak-carckers-backend-api.onrender.com/api/reviews';

const UserReviewsPage = () => {
  const { isDark } = useContext(ThemeContext);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newReview, setNewReview] = useState({ name: '', rating: 5, text: '' });
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch reviews from backend on page load
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await axios.get(API_URL);
        setReviews(res.data);
      } catch (err) {
        console.error('Failed to fetch reviews:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  const handleAddReview = async (e) => {
    e.preventDefault();
    if (!newReview.name || !newReview.text) return;

    setIsSubmitting(true);
    try {
      const res = await axios.post(API_URL, {
        name: newReview.name,
        rating: newReview.rating,
        text: newReview.text,
      });

      // Backend la save aana review-a list top la add pannurom
      setReviews([res.data, ...reviews]);
      setNewReview({ name: '', rating: 5, text: '' });
      setShowForm(false);
      alert('Review added successfully!');
    } catch (err) {
      console.error('Failed to save review:', err);
      alert('Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const avgRating = reviews.length
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : '0.0';

  return (
    <div className={`page-shell ${isDark ? 'bg-dark text-light' : ''}`} style={{ minHeight: '100vh' }}>
      <Navbar />
      <div className="container py-5">
        <h1 className="mb-4 text-center">
          <FaStar className="text-warning me-2" /> Customer Reviews
        </h1>

        {/* Rating Summary */}
        <div className={`card mb-5 ${isDark ? 'bg-dark-card border-secondary' : ''}`} style={isDark ? { backgroundColor: '#1e1e1e', borderColor: '#444' } : {}}>
          <div className="card-body text-center">
            <div className="display-3 fw-bold text-warning mb-2">{avgRating} / 5</div>
            <div className="mb-3">
              {[...Array(5)].map((_, i) => (
                <FaStar key={i} className={i < Math.round(avgRating) ? 'text-warning' : 'text-secondary'} />
              ))}
            </div>
            <p className={`mb-0 ${isDark ? 'text-muted' : 'text-muted'}`}>{reviews.length} verified reviews</p>
          </div>
        </div>

        {/* Add Review Button */}
        <div className="text-center mb-4">
          <button
            className="btn btn-danger"
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? 'Cancel' : '+ Write a Review'}
          </button>
        </div>

        {/* Add Review Form */}
        {showForm && (
          <div className={`card mb-5 ${isDark ? 'bg-dark-card border-secondary' : ''}`} style={isDark ? { backgroundColor: '#1e1e1e', borderColor: '#444' } : {}}>
            <div className="card-body">
              <h5 className={`card-title mb-3 ${isDark ? 'text-light' : ''}`}>Share Your Experience</h5>
              <form onSubmit={handleAddReview}>
                <div className="mb-3">
                  <label className={`form-label ${isDark ? 'text-light' : ''}`}>Your Name</label>
                  <input
                    type="text"
                    className={`form-control ${isDark ? 'bg-dark text-light border-secondary' : ''}`}
                    placeholder="Enter your name"
                    value={newReview.name}
                    onChange={(e) => setNewReview({ ...newReview, name: e.target.value })}
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div className="mb-3">
                  <label className={`form-label ${isDark ? 'text-light' : ''}`}>Rating</label>
                  <select
                    className={`form-select ${isDark ? 'bg-dark text-light border-secondary' : ''}`}
                    value={newReview.rating}
                    onChange={(e) => setNewReview({ ...newReview, rating: parseInt(e.target.value) })}
                    disabled={isSubmitting}
                  >
                    <option value={5}>5 Stars - Excellent</option>
                    <option value={4}>4 Stars - Good</option>
                    <option value={3}>3 Stars - Average</option>
                    <option value={2}>2 Stars - Below Average</option>
                    <option value={1}>1 Star - Poor</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className={`form-label ${isDark ? 'text-light' : ''}`}>Your Review</label>
                  <textarea
                    className={`form-control ${isDark ? 'bg-dark text-light border-secondary' : ''}`}
                    placeholder="Share your experience..."
                    rows="4"
                    value={newReview.text}
                    onChange={(e) => setNewReview({ ...newReview, text: e.target.value })}
                    required
                    disabled={isSubmitting}
                  ></textarea>
                </div>
                <button type="submit" className="btn btn-danger w-100" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit Review'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Reviews List */}
        {loading ? (
          <p className="text-center">Loading reviews...</p>
        ) : reviews.length === 0 ? (
          <p className="text-center">No reviews yet. Be the first to write one!</p>
        ) : (
          <div className="row">
            {reviews.map((review) => (
              <div key={review._id || review.id} className="col-lg-6 col-md-12 mb-4">
                <div
                  className={`card h-100 ${isDark ? 'border-secondary' : ''}`}
                  style={isDark ? { backgroundColor: '#1e1e1e', borderColor: '#444' } : {}}
                >
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <h5 className={`card-title d-flex align-items-center ${isDark ? 'text-light' : ''}`}>
                          <FaUser className="me-2 text-info" /> {review.name}
                        </h5>
                        <div className="text-warning mb-2">
                          {[...Array(5)].map((_, i) => (
                            <FaStar key={i} className={i < review.rating ? 'text-warning' : 'text-secondary'} />
                          ))}
                        </div>
                      </div>
                    </div>

                    <p className={`card-text ${isDark ? 'text-light' : ''}`}>{review.text}</p>

                    <div className={`d-flex justify-content-between align-items-center pt-3 border-top ${isDark ? 'border-secondary' : ''}`}>
                      <small className={isDark ? 'text-muted' : 'text-muted'}>
                        <FaCalendar className="me-1" />{' '}
                        {new Date(review.createdAt || review.date).toLocaleDateString()}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserReviewsPage;